import { Injectable } from '@angular/core';
import { LoginLibService } from '@mx51/login-lib';

/**
 * Query key guidelines:
 * 1. Keys used pre-authentication belong in the public keys prop.
 * 2. Keys used post-authentication belong in the secure keys prop. These keys will be prefixed with the current
 *    session id automatically. This ensures sensitive data cannot leak between user sessions.
 * 3. Branch keys by feature or type of thing at the most granular level that makes sense. Specify an all() key. The
 *    all() key should only serve as the base for other keys and not be called directly except when invalidating
 *    queries. Invalidating via the all() key is especially useful for invalidating all cached pages of a paginated
 *    list.
 *
 *    Do - branch by feature:
 *    ```
 *    {
 *      disputes: {
 *        all: () => ['disputes']
 *      }
 *      transactions: {
 *        all: () => ['transactions']
 *      }
 *     }
 *     ```
 *
 *    Avoid - mixing features:
 *    ```
 *    {
 *      disputes: () => ['disputes']
 *      transactions: () => ['transactions']
 *    }
 *    ```
 *
 *    Do - use the all() key as a base for other keys:
 *    ```
 *    {
 *      disputes: {
 *        all: () => ['disputes'],
 *        list: () => [...disputes.all(), 'list']
 *      }
 *    }
 *    ```
 *
 *    Do - query keys for specific actions:
 *    ```
 *    queryService.useQuery(queryKeys.disputes.list(), () => {})
 *    ```
 *
 *    Avoid - querying the all() key directly:
 *    ```
 *    queryService.useQuery(queryKeys.disputes.all(), () => {})
 *    ```
 *
 *    Do - leverage the all() key to invalidate ALL queries for a feature:
 *    ```
 *    queryService.invalidateQueries(queryKeys.disputes.all())
 *    ```
 *
 *  4. Branch individual entities separately. Require the entity id as a parameter at the root of the branch. For
 *     individual entities specify a byId() key instead of an all(). The byId() key should be used to get an entity by
 *     id.
 *
 *     Do - branch individual entities separately:
 *     ```
 *     {
 *      dispute: (disputeId: string) => {
 *        byId: () => ['dispute', disputeId]
 *      },
 *      disputes: {
 *        all: () => ['disputes'],
 *        list: () => [...disputes.all(), 'list']
 *      }
 *     }
 *     ```
 *
 *     Avoid - mixing individual entities with features:
 *     ```
 *     {
 *      disputes: {
 *        all: () => ['disputes'],
 *        byId: (disputeId: string) => [...disputes.all(), disputeId],
 *        list: () => [...disputes.all(), 'list']
 *      }
 *     }
 *     ```
 *
 *    Do - query the byId() key directly:
 *    ```
 *    queryService.useQuery(queryKeys.dispute(disputeId).byId(), () => {})
 *    ```
 *
 *   Do - leverage the byId() key to invalidate ALL queries for an entity:
 *   ```
 *   queryService.useQuery(queryKeys.dispute(disputeId).byId(), () => {})
 *   ```
 *
 * 5. The byId() key should also serve as the base for any other keys directly related to the entity.
 *
 *   Do - branch related entities off of the byId() key:
 *   ```
 *   {
 *    dispute: (disputeId: string) => {
 *      byId: () => ['dispute', disputeId]
 *      comment: () => [...dispute(disputeId).byId(), 'comment']
 *    }
 *   }
 *   ```
 *
 * 6. Nest to any level to keep things organised.
 *
 *  Avoid - lumping all keys in a single bucket:
 *   ```
 *  {
 *    dispute: (disputeId: string) => {
 *      byId: () => ['dispute', disputeId]
 *      listActivities: () => [...dispute(disputeId).byId(), 'list-activities']
 *      searchActivities: () => [...dispute(disputeId).byId(), 'search-activities']
 *    }
 *  }
 *  ```
 *
 *  Do - nest keys when required:
 *  ```
 *  {
 *    dispute: (disputeId: string) => {
 *      byId: () => ['dispute', disputeId]
 *      activities: {
 *        all: () => [...dispute(disputeId).byId(), 'activities'],
 *        list: () => [...dispute(disputeId).activities.all(), 'list']
 *        search: () => [...dispute(disputeId).activities.all(), 'search']
 *      }
 *    }
 *  }
 *  ```
 *
 * 7. Only add keys for mutations if you have a specific reason to do so. The two use cases for mutation keys are
 *    inheriting defaults and debugging.
 */
@Injectable({
  providedIn: 'root',
})
export class QueryKeys {
  public = {}; // This is where I would put my public query keys, if I had any!!
  secure = {
    all: ['session', this.sessionId],
    alert: (ticketNumber: number) => {
      return {
        byTicketNumber: () => [...this.secure.all, 'alert', ticketNumber],
      };
    },
    alerts: {
      all: () => [...this.secure.all, 'alerts'],
      assignees: {
        all: () => [...this.secure.alerts.all(), 'assignees'],
        list: () => [...this.secure.alerts.assignees.all(), 'list'],
      },
      countOutstanding: () => [...this.secure.alerts.all(), 'count-outstanding'],
      types: {
        all: () => [...this.secure.alerts.all(), 'types'],
        list: () => [...this.secure.alerts.types.all(), 'list'],
      },
    },
    featureFlags: () => [...this.secure.all, 'feature-flags'],
    ledger: {
      all: () => [...this.secure.all, 'ledger'],
      merchantFacility: (merchantFacilityId: string) => {
        return {
          byId: () => [...this.secure.ledger.all(), 'merchant-facility', merchantFacilityId],
          ledgerEntries: (options: object) => [
            ...this.secure.ledger.merchantFacility(merchantFacilityId).byId(),
            'ledger-entries',
            options,
          ],
        };
      },
      systemSettings: () => [...this.secure.ledger.all(), 'system-settings'],
    },
    org: (orgId: string) => {
      return {
        byId: () => [...this.secure.all, 'organisation', orgId],
        invitations: {
          all: () => [...this.secure.org(orgId).byId(), 'invitations'],
          list: (options: object) => [...this.secure.org(orgId).invitations.all(), 'list', options],
        },
        members: {
          all: () => [...this.secure.org(orgId).byId(), 'members'],
          audit: (options: object) => [...this.secure.org(orgId).members.all(), 'audit', options],
          list: (options: object) => [...this.secure.org(orgId).members.all(), 'list', options],
        },
        tapApp: {
          all: () => [...this.secure.org(orgId).byId(), 'tap-app'],
          alerts: (options: object) => [...this.secure.org(orgId).tapApp.all(), 'alerts', options],
          audit: (options: object) => [...this.secure.org(orgId).tapApp.all(), 'audit', options],
          members: () => [...this.secure.org(orgId).tapApp.all(), 'members'],
        },
        merchantFacility: (merchantFacilityId: string) => {
          return {
            byId: () => [...this.secure.org(orgId).byId(), 'merchant-facility', merchantFacilityId],
            dispute: (disputeId: string) => {
              return {
                byId: () => [
                  ...this.secure.org(orgId).merchantFacility(merchantFacilityId).byId(),
                  'dispute',
                  disputeId,
                ],
                activities: {
                  all: () => [
                    ...this.secure.org(orgId).merchantFacility(merchantFacilityId).dispute(disputeId).byId(),
                    'activities',
                  ],
                  list: () => [
                    ...this.secure.org(orgId).merchantFacility(merchantFacilityId).dispute(disputeId).activities.all(),
                    'list',
                  ],
                },
                evidences: {
                  all: () => [
                    ...this.secure.org(orgId).merchantFacility(merchantFacilityId).dispute(disputeId).byId(),
                    'evidences',
                  ],
                  list: () => [
                    ...this.secure.org(orgId).merchantFacility(merchantFacilityId).dispute(disputeId).evidences.all(),
                    'list',
                  ],
                },
              };
            },
            logo: (fileId: string) => {
              return {
                byFileId: () => [...this.secure.org(orgId).merchantFacility(merchantFacilityId).byId(), 'logo', fileId],
              };
            },
            terminal: (terminalId: string) => {
              return {
                byId: () => [
                  ...this.secure.org(orgId).merchantFacility(merchantFacilityId).byId(),
                  'terminal',
                  terminalId,
                ],
                audit: () => [
                  ...this.secure.org(orgId).merchantFacility(merchantFacilityId).terminal(terminalId).byId(),
                  'audit',
                ],
                deviceAssignments: () => [
                  ...this.secure.org(orgId).merchantFacility(merchantFacilityId).terminal(terminalId).byId(),
                  'device-assignments',
                ],
                hierarchy: () => [
                  ...this.secure.org(orgId).merchantFacility(merchantFacilityId).terminal(terminalId).byId(),
                  'hierarchy',
                ],
                spiCloudPairings: (options: object) => [
                  ...this.secure.org(orgId).merchantFacility(merchantFacilityId).terminal(terminalId).byId(),
                  'spi-cloud-pairings',
                  options,
                ],
                healthTransactions: {
                  all: () => [
                    ...this.secure.org(orgId).merchantFacility(merchantFacilityId).terminal(terminalId).byId(),
                    'health-transactions',
                  ],
                  list: (options?: object) => [
                    ...this.secure
                      .org(orgId)
                      .merchantFacility(merchantFacilityId)
                      .terminal(terminalId)
                      .healthTransactions.all(),
                    'list',
                    options,
                  ],
                },
              };
            },
            idvRecords: {
              all: () => [...this.secure.org(orgId).merchantFacility(merchantFacilityId).byId(), 'idv-records'],
              list: (options?: object) => [
                ...this.secure.org(orgId).merchantFacility(merchantFacilityId).idvRecords.all(),
                'list',
                options,
              ],
            },
            payouts: {
              all: () => [...this.secure.org(orgId).merchantFacility(merchantFacilityId).byId(), 'payouts'],
              list: (options?: object) => [
                ...this.secure.org(orgId).merchantFacility(merchantFacilityId).payouts.all(),
                'list',
                options,
              ],
            },
            adjustment: (adjustmentId: string) => {
              return {
                byId: () => [
                  ...this.secure.org(orgId).merchantFacility(merchantFacilityId).byId(),
                  'adjustment',
                  adjustmentId,
                ],
              };
            },
            adjustments: {
              all: () => [...this.secure.org(orgId).merchantFacility(merchantFacilityId).byId(), 'adjustments'],
              list: (options?: object) => [
                ...this.secure.org(orgId).merchantFacility(merchantFacilityId).adjustments.all(),
                'list',
                options,
              ],
            },
            msfSchedules: {
              all: () => [...this.secure.org(orgId).merchantFacility(merchantFacilityId).byId(), 'msf-schedules'],
              list: (options?: object) => [
                ...this.secure.org(orgId).merchantFacility(merchantFacilityId).msfSchedules.all(),
                'list',
                options,
              ],
              msfTemplate: () => [...this.secure.org(orgId).merchantFacility(merchantFacilityId).byId(), 'template'],
            },
            apexRewardsSubscriptions: {
              all: () => [
                ...this.secure.org(orgId).merchantFacility(merchantFacilityId).byId(),
                'apex-rewards-subscriptions',
              ],
              list: (options?: object) => [
                ...this.secure.org(orgId).merchantFacility(merchantFacilityId).apexRewardsSubscriptions.all(),
                'list',
                options,
              ],
            },
            ledger: {
              all: () => [...this.secure.org(orgId).merchantFacility(merchantFacilityId).byId(), 'ledger'],
              settings: () => [...this.secure.org(orgId).merchantFacility(merchantFacilityId).ledger.all(), 'settings'],
            },
            settlementBankAccount: () => [
              ...this.secure.org(orgId).merchantFacility(merchantFacilityId).byId(),
              'settlement-bank-account',
            ],
            riskFields: () => [...this.secure.org(orgId).merchantFacility(merchantFacilityId).byId(), 'risk-fields'],
            healthTransactions: {
              all: () => [...this.secure.org(orgId).merchantFacility(merchantFacilityId).byId(), 'health-transactions'],
              list: (options?: object) => [
                ...this.secure.org(orgId).merchantFacility(merchantFacilityId).healthTransactions.all(),
                'list',
                options,
              ],
            },
          };
        },
        merchantFacilities: {
          all: () => [...this.secure.org(orgId).byId(), 'merchant-facilities'],
          list: (options: object) => [...this.secure.org(orgId).merchantFacilities.all(), 'list', options],
        },
        vasEnrolments: {
          all: () => [...this.secure.org(orgId).byId(), 'vas-enrolments'],
          list: () => [...this.secure.org(orgId).vasEnrolments.all(), 'list'],
        },
        posConfigs: {
          hnl: {
            pat: {
              clientCredentials: () => [
                ...this.secure.org(orgId).byId(),
                'pos-configs',
                'hnl',
                'pat',
                'client-credentials',
              ],
            },
          },
        },
      };
    },
    user: (userId: string) => {
      return {
        byId: () => [...this.secure.all, 'user', userId],
        invitations: {
          all: () => [...this.secure.user(userId).byId(), 'invitations'],
          list: (options: object) => [...this.secure.user(userId).invitations.all(), 'list', options],
        },
        orgMemberships: {
          all: () => [...this.secure.user(userId).byId(), 'org-memberships'],
          list: (options: object) => [...this.secure.user(userId).orgMemberships.all(), 'list', options],
        },
      };
    },
    users: {
      all: () => [...this.secure.all, 'users'],
      search: (options: object) => [...this.secure.users.all(), 'search', options],
    },
    terminals: {
      all: () => [...this.secure.all, 'terminals'],
      list: (options: object) => [...this.secure.terminals.all(), 'list', options],
    },
    organisations: {
      all: () => [...this.secure.all, 'organisations'],
      list: (options: object) => [...this.secure.organisations.all(), 'list', options],
    },
    merchantFacilities: {
      all: () => [...this.secure.all, 'merchant-facilities'],
      list: (options: object) => [...this.secure.merchantFacilities.all(), 'list', options],
    },
    merchantApplications: {
      all: () => [...this.secure.all, 'merchant-applications'],
      list: (options: object) => [...this.secure.merchantApplications.all(), 'list', options],
    },
    merchantApplication: (id: string) => {
      return {
        byId: () => [...this.secure.all, 'merchant-application', id],
      };
    },
    subTenantOrganisations: {
      all: () => [...this.secure.all, 'sub-tenant-organisations'],
      list: (options: object) => [...this.secure.subTenantOrganisations.all(), 'list', options],
    },
    payout: (payoutId: string) => ({
      byId: () => [...this.secure.all, 'payout', payoutId],
    }),
    payoutTransfer: (transferId: string) => ({
      byId: () => [...this.secure.all, 'payout-transfer', transferId],
    }),
    payouts: {
      all: () => [...this.secure.all, 'payouts'],
      list: (options?: object) => [...this.secure.payouts.all(), 'list', options],
    },
    salesCalculatorData: (averageTransactionValue?: number) => [
      ...this.secure.all,
      'sales-calculator-data',
      averageTransactionValue ?? '',
    ],
    cronJobs: {
      all: () => [...this.secure.all, 'cron-jobs'],
      list: () => [...this.secure.cronJobs.all(), 'list'],
    },
  };

  private get sessionId(): string {
    return this.loginLibService.getSessionId();
  }

  constructor(private loginLibService: LoginLibService) {}
}
